import cl from 'clsx'
import { ButtonHTMLAttributes, forwardRef } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import { AnalyticsTableRowType } from '@/features/analytics/types'

import classes from './MarkingCell.module.scss'

import Clock from '@/features/analytics/invite-links/icons/stones/Clock.svg?react'
import Diagram from '@/features/analytics/invite-links/icons/stones/Diagram.svg?react'
import Document from '@/features/analytics/invite-links/icons/stones/Document.svg?react'
import Dollar from '@/features/analytics/invite-links/icons/stones/Dollar.svg?react'
import Erid from '@/features/analytics/invite-links/icons/stones/Erid.svg?react'
import Image from '@/features/analytics/invite-links/icons/stones/Image.svg?react'
import Pencil from '@/features/analytics/invite-links/icons/stones/Pencil.svg?react'
import Person from '@/features/analytics/invite-links/icons/stones/Person.svg?react'

const MARKING_FLAGS = [
    { flag: 'publish_option_flag', Icon: Clock, text: 'Параметры публикации' },
    { flag: 'publisher_flag', Icon: Person, text: 'Издатель' },
    { flag: 'contract_flag', Icon: Document, text: 'Договор' },
    { flag: 'invoice_flag', Icon: Dollar, text: 'Счет и оплата' },
    { flag: 'creative_flag', Icon: Erid, text: 'Ссылка и erid' },
    { flag: 'published_post_flag', Icon: Image, text: 'Опубликованный пост' },
    { flag: 'act_flag', Icon: Pencil, text: 'Акт' },
    { flag: 'ord_status_flag', Icon: Diagram, text: 'Статус ОРД' },
] as const

export type MarkingModalTriggerProps = {
    markingFormData: ORDMarkingFormData
    analyticsTableRowType: AnalyticsTableRowType
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'>
export const MarkingModalTrigger = forwardRef<
    HTMLButtonElement,
    MarkingModalTriggerProps
>(function MarkingModalTrigger(
    { markingFormData, className, analyticsTableRowType, ...buttonProps },
    ref
) {
    // if link deleted at is not null, then isDeleted is true
    const isDeleted = !!analyticsTableRowType.publisher?.link_deleted_at

    // If current date is greater than deleted date + 1, and post is not deleted, show error
    const publishedPostFlagError =
        !isDeleted &&
        markingFormData.deleted_date &&
        new Date(markingFormData.deleted_date).getTime() + 24 * 60 * 60 * 1000 <
            new Date().getTime()

    // If creative_flag is true, but contract_flag is false, then show error
    const contractFlagError =
        markingFormData.creative_flag && !markingFormData.contract_flag

    const flags: Record<
        (typeof MARKING_FLAGS)[number]['flag'],
        boolean | 'error' | 'waiting'
    > = {
        contract_flag: contractFlagError
            ? 'error'
            : markingFormData.contract_flag,
        publisher_flag: markingFormData.publisher_flag,
        invoice_flag: markingFormData.invoice_flag,
        publish_option_flag: markingFormData.publish_option_flag,
        published_post_flag: publishedPostFlagError
            ? 'error'
            : !!analyticsTableRowType.publisher?.link_deleted_at,
        creative_flag: markingFormData.creative_flag,
        act_flag:
            markingFormData.act_upload_scheduled_at &&
            new Date(markingFormData.act_upload_scheduled_at) >= new Date()
                ? 'waiting'
                : markingFormData.act_flag,
        ord_status_flag: markingFormData.statistics_flag
            ? true
            : isDeleted
              ? 'error'
              : false,
    }

    return (
        <button
            {...buttonProps}
            ref={ref}
            type="button"
            className={cl(classes.control, classes.formDataFlagsRow, className)}
        >
            {MARKING_FLAGS.map(({ flag, Icon, text }, index) => {
                return (
                    <Tooltip key={flag ?? index} delayDuration={300}>
                        <TooltipTrigger asChild>
                            <Icon
                                className={cl(classes.formDataFlag, {
                                    [classes.checked]:
                                        flag && flags[flag] === true,
                                    [classes.error]:
                                        flag && flags[flag] === 'error',
                                    [classes.waiting]:
                                        flag && flags[flag] === 'waiting',
                                })}
                            />
                        </TooltipTrigger>
                        <TooltipContent side="top">{text}</TooltipContent>
                    </Tooltip>
                )
            })}
        </button>
    )
})
